import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { StudyService } from 'src/app/shared-new/services/study.service';
import { HttpErrorHandlerService } from '../../../services/http-error-handler.service';
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import {
  CancelDraftSubmission,
  CancelDraftSubmissionFailure,
  CancelDraftSubmissionSuccess,
  CancelStudySubmission,
  CancelStudySubmissionFailure,
  CancelStudySubmissionSuccess,
  CancelSubmissionById,
  CancelSubmissionByIdFailure,
  CancelSubmissionByIdSuccess,
  GetDraftSubmissionsCount,
  GetDraftSubmissionsCountFailure,
  GetDraftSubmissionsCountSuccess,
  GetSubmissionsByStudyId,
  GetSubmissionsByStudyIdFailure,
  GetSubmissionsByStudyIdSuccess,
  GetUserSubmissions,
  GetUserSubmissionsFailure,
  GetUserSubmissionsSuccess,
} from './submissions.action';

@Injectable()
export class SubmissionsEffect {
  constructor(
    private actions$: Actions,
    private studyService: StudyService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private httpErrorHandlerSerivce: HttpErrorHandlerService
  ) {}

  loadSubmissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetUserSubmissions),
      tap(() => this.spinnerService.addRequest('loadSubmissions')),
      mergeMap(() => {
        return this.studyService.getUserSubmissions().pipe(
          map((submissions) => {
            this.spinnerService.popRequest('loadSubmissions');
            return GetUserSubmissionsSuccess({ submissions });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadSubmissions');
            return of(GetUserSubmissionsFailure({ err }));
          })
        );
      })
    )
  );

  loadSubmissionsByStudyId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetSubmissionsByStudyId),
      tap(() => this.spinnerService.addRequest('loadSubmissionsByStudyId')),
      mergeMap((model) => {
        return this.studyService.getSubmissionByStudyId(model.studyId).pipe(
          map((submissions) => {
            this.spinnerService.popRequest('loadSubmissionsByStudyId');
            return GetSubmissionsByStudyIdSuccess({ submissions });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadSubmissionsByStudyId');
            return of(GetSubmissionsByStudyIdFailure({ err }));
          })
        );
      })
    )
  );

  cancelSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CancelSubmissionById),
      tap(() => this.spinnerService.addRequest('cancelSubmission')),
      mergeMap((model) => {
        return this.studyService
          .cancelStudySubmissionById(
            model.saleId,
            model.horseId,
            model.submissionId
          )
          .pipe(
            map(() => {
              this.spinnerService.popRequest('cancelSubmission');
              this.toastService.success(
                'Submission has been cancelled successfully!',
                'Study'
              );
              return CancelSubmissionByIdSuccess({
                submissionId: model.submissionId,
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('cancelSubmission');
              this.httpErrorHandlerSerivce.ShowErrorMessage(err);
              return of(CancelSubmissionByIdFailure({ err }));
            })
          );
      })
    )
  );

  cancelStudySubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CancelStudySubmission),
      tap(() => this.spinnerService.addRequest('cancelStudySubmission')),
      mergeMap((model) => {
        return this.studyService
          .cancelStudySubmission(model.submission, model.studyId)
          .pipe(
            map(() => {
              this.spinnerService.popRequest('cancelStudySubmission');
              this.toastService.success(
                'Submission has been cancelled successfully!',
                'Study'
              );
              return CancelStudySubmissionSuccess({
                submissionId: model.submission.id,
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('cancelStudySubmission');
              this.httpErrorHandlerSerivce.ShowErrorMessage(err);
              return of(CancelStudySubmissionFailure({ err }));
            })
          );
      })
    )
  );

  cancelDraftSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CancelDraftSubmission),
      tap(() => this.spinnerService.addRequest('cancelDraftSubmission')),
      mergeMap((model) => {
        return this.studyService
          .cancelSubmission(model.saleId, model.horseId)
          .pipe(
            map(() => {
              this.spinnerService.popRequest('cancelDraftSubmission');
              this.toastService.success(
                'Submission has been cancelled successfully!',
                'Study'
              );
              return CancelDraftSubmissionSuccess({
                submissionId: 0,
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('cancelDraftSubmission');
              this.httpErrorHandlerSerivce.ShowErrorMessage(err);
              return of(CancelDraftSubmissionFailure({ err }));
            })
          );
      })
    )
  );

  getDraftSubmissionCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetDraftSubmissionsCount),
      mergeMap(() => {
        return this.studyService.getDraftSubmissionCount().pipe(
          map((draftSubmissionCount) => {
            return GetDraftSubmissionsCountSuccess({
              draftSubmissionCount,
            });
          }),
          catchError((err) => {
            return of(GetDraftSubmissionsCountFailure({ err }));
          })
        );
      })
    )
  );
}
